import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from '../supabaseClient';

export interface VersionInfo {
  version: string;
  build: number;
}

export interface RemoteVersionInfo {
  platform?: string;
  latest_version: string;
  latest_build?: number;
  min_version?: string;
  min_version_android?: string;
  min_version_ios?: string;
  min_build?: number;
  store_url?: string;
  store_url_android?: string;
  store_url_ios?: string;
  is_active?: boolean;
  message?: string;
}

export interface VersionCheckResult {
  hasUpdate: boolean;
  isMandatory: boolean;
  installed: VersionInfo;
  latest: RemoteVersionInfo;
  storeUrl: string;
}

// Fallback local cuando se ejecuta en Web
export const LOCAL_VERSION: VersionInfo = {
  version: '2.0.9',
  build: 70
};

/**
 * Obtiene la versión instalada actualmente.
 * En plataformas nativas consulta la información real de Capacitor (App.getInfo),
 * en Web utiliza la versión inyectada o LOCAL_VERSION.
 */
export async function getInstalledVersion(): Promise<VersionInfo> {
  if (Capacitor.isNativePlatform()) {
    try {
      const info = await CapacitorApp.getInfo();
      return {
        version: info.version,
        build: parseInt(info.build, 10) || 0
      };
    } catch (e) {
      console.error('[versionCheck] Error al obtener info de Capacitor:', e);
    }
  }
  return LOCAL_VERSION;
}

/**
 * Compara dos versiones semánticas número por número (ej: "1.5.36" vs "1.5.9").
 * Retorna:
 *  1 si v1 > v2
 * -1 si v1 < v2
 *  0 si v1 == v2
 */
export function compareVersions(v1: string, v2: string): number {
  const parts1 = String(v1).split('.').map(p => parseInt(p, 10) || 0);
  const parts2 = String(v2).split('.').map(p => parseInt(p, 10) || 0);

  const maxLength = Math.max(parts1.length, parts2.length);
  for (let i = 0; i < maxLength; i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

/**
 * Consulta la versión remota configurada en Supabase (tabla app_version_control).
 * Si no responde, no hay conexión o la tabla no está creada, retorna null
 * silenciosamente sin bloquear la aplicación.
 */
export async function fetchLatestVersion(): Promise<RemoteVersionInfo | null> {
  const platform = Capacitor.getPlatform(); // 'android' | 'ios' | 'web'

  try {
    const { data, error } = await supabase
      .from('app_version_control')
      .select('*')
      .or(`platform.eq.${platform},platform.eq.all`)
      .eq('is_active', true)
      .maybeSingle();

    if (!error && data) {
      return data as RemoteVersionInfo;
    }
  } catch (err) {
    // Falla silenciosa si no existe la tabla o no hay conexión
  }

  return null;
}

/**
 * Ejecuta el chequeo completo de versiones y retorna el diagnóstico:
 * - isMandatory: si la versión instalada es inferior a min_version_android o min_version_ios
 * - hasUpdate: si la versión latest_version es superior a la instalada
 * - Sin conexión o en caso de error, retorna null sin bloquear la app.
 */
export async function checkForUpdate(): Promise<VersionCheckResult | null> {
  // En la web no aplica la actualización obligatoria desde tienda
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  try {
    const installed = await getInstalledVersion();
    const remote = await fetchLatestVersion();
    if (!remote || remote.is_active === false) return null;

    const platform = Capacitor.getPlatform(); // 'android' | 'ios'

    // Resolver versión mínima exclusivamente según la plataforma nativa
    const minVersion = platform === 'ios'
      ? (remote.min_version_ios || '0.0.0')
      : (remote.min_version_android || '0.0.0');

    // Resolver URL de la tienda según la plataforma
    const storeUrl = platform === 'ios'
      ? (remote.store_url_ios || remote.store_url || 'https://apps.apple.com/app/id6774316076')
      : (remote.store_url_android || remote.store_url || 'https://play.google.com/store/apps/details?id=cl.ldconstructora.klydapp');

    // 1. Comparar versión con la mínima obligatoria (bloqueo total si installed < minVersion)
    const minComparison = compareVersions(installed.version, minVersion);
    const isMandatory = minComparison < 0;

    // 2. Comparar versión con la última versión disponible (aviso suave si latest_version > installed)
    const latestComparison = compareVersions(remote.latest_version, installed.version);
    const hasUpdate = latestComparison > 0;

    if (!isMandatory && !hasUpdate) {
      return null;
    }

    return {
      hasUpdate,
      isMandatory,
      installed,
      latest: remote,
      storeUrl
    };
  } catch (e) {
    console.warn('[versionCheck] Error en comprobación de versiones (no bloqueante):', e);
  }

  return null;
}
